import { isNil } from 'lodash-es';

import { type HexColor } from '../types/utility.types';

import { round } from './math.helpers';
import { getObjectKeys } from './object.helpers';

interface RgbColor<Value extends string | number = number> {
  r: Value;
  g: Value;
  b: Value;
}

export function getOpacityHex(opacity: number): string {
  if (isNil(opacity)) {
    return '';
  }

  return Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0');
}

export function opacifyHex(hexColor: string, opacity: number): string {
  if (isNil(opacity)) {
    return '';
  }

  if (!/^#(?:[0-9a-fA-F])(?:.{5}|.{7})$/.test(hexColor)) {
    throw new Error(`Invalid hex color: ${hexColor}`);
  }

  // Convert the opacity to an alpha value (0 to 255)
  const opacityHex = Math.floor(opacity * 255).toString(16);
  const formattedOpacityHex =
    opacityHex.length === 1 ? '0' + opacityHex : opacityHex;

  // Generate the RGBA color based on the hex color (with the alpha value if an old one was present)
  return `${hexColor.slice(0, 7)}${formattedOpacityHex}`;
}

export function isHexColor(color: unknown): boolean {
  return (
    !!color &&
    typeof color === 'string' &&
    color.length === 6 &&
    !isNaN(parseInt(color, 16))
  );
}

export function darkenColor(color: string, percentage: number = 60): HexColor {
  const rgbColor = hexToRgb(color);

  for (const key of getObjectKeys(rgbColor)) {
    rgbColor[key] = round(((100 - percentage) / 100) * rgbColor[key]);
  }

  return rgbToHex(rgbColor);
}

function hexToRgb(hexColor: string): RgbColor {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColor);

  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : {
        r: 0,
        g: 0,
        b: 0,
      };
}

function rgbToHex(rgbColor: RgbColor): HexColor {
  const hexColor: RgbColor<string> = {
    r: '',
    g: '',
    b: '',
  };

  for (const key of getObjectKeys(rgbColor)) {
    hexColor[key] = rgbColor[key].toString(16);

    if (hexColor[key].length < 2) {
      hexColor[key] = `0${hexColor[key]}`;
    }
  }

  return `#${hexColor.r}${hexColor.g}${hexColor.b}`;
}
